import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

type DatePickerMode = 'single' | 'range'

type DatePickerSize = 'default' | 'compact'

interface ChatbotDatePickerBaseProps {
  onSkip?: () => void
  showSkip?: boolean
  maxDate?: Date
  minDate?: Date
  theme?: 'light' | 'dark'
  mode?: DatePickerMode
  rangeHint?: string
  size?: DatePickerSize
}

type ChatbotDatePickerSingleProps = ChatbotDatePickerBaseProps & {
  mode?: 'single'
  onDateSelect: (date: Date) => void
  onDateRangeSelect?: never
}

type ChatbotDatePickerRangeProps = ChatbotDatePickerBaseProps & {
  mode: 'range'
  onDateRangeSelect: (start: Date, end: Date) => void
  onDateSelect?: never
}

type ChatbotDatePickerProps = ChatbotDatePickerSingleProps | ChatbotDatePickerRangeProps

function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function getSizeStyles(compact: boolean) {
  if (compact) {
    return {
      container: {
        padding: '8px 10px',
        marginTop: '8px',
        borderRadius: '6px',
        maxWidth: 'min(100%, 272px)',
        marginLeft: 'auto',
        marginRight: 'auto',
        boxShadow: 'none',
      },
      hint: {fontSize: '11px', marginBottom: '6px'},
      header: {gap: '6px', marginBottom: '6px'},
      select: {padding: '2px 6px', fontSize: '12px'},
      weekday: {fontSize: '10px', padding: '2px 0'},
      gridGap: '2px',
      emptyCellHeight: 24,
      day: {padding: '3px 2px', fontSize: '12px', minHeight: 24},
      skipMarginTop: '8px',
      skipButton: {fontSize: '12px', padding: '2px 6px'},
      weekDays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    }
  }

  return {
    container: {
      padding: '16px',
      marginTop: '12px',
      borderRadius: '8px',
      maxWidth: '100%',
      marginLeft: undefined,
      marginRight: undefined,
      boxShadow: undefined,
    },
    hint: {fontSize: '12px', marginBottom: '10px'},
    header: {gap: '8px', marginBottom: '12px'},
    select: {padding: '4px 8px', fontSize: '14px'},
    weekday: {fontSize: '12px', padding: '4px'},
    gridGap: '4px',
    emptyCellHeight: 36,
    day: {padding: '8px', fontSize: '14px', minHeight: 36},
    skipMarginTop: '12px',
    skipButton: {fontSize: '14px', padding: '4px 8px'},
    weekDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  }
}

const ChatbotDatePicker: React.FC<ChatbotDatePickerProps> = (props) => {
  const {
    onSkip,
    showSkip = false,
    maxDate,
    minDate,
    theme = 'light',
    mode = 'single',
    rangeHint,
    size = 'default',
  } = props

  const onDateSelect = mode === 'single' ? props.onDateSelect : undefined
  const onDateRangeSelect = mode === 'range' ? props.onDateRangeSelect : undefined

  const intl = useIntl()
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>(theme)

  useEffect(() => {
    const detectTheme = () => {
      const bodyTheme = document.body.getAttribute('data-bs-theme')
      const htmlTheme = document.documentElement.getAttribute('data-bs-theme')
      const detectedTheme = bodyTheme || htmlTheme || theme
      setActualTheme(detectedTheme === 'dark' ? 'dark' : 'light')
    }

    detectTheme()

    const observer = new MutationObserver(detectTheme)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })

    return () => observer.disconnect()
  }, [theme])

  const isDark = actualTheme === 'dark'
  const currentDate = useMemo(() => new Date(), [])
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [rangeStart, setRangeStart] = useState<Date | null>(null)
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null)

  const yearOptions = useMemo(() => {
    const currentYear = currentDate.getFullYear()
    const years: number[] = []
    for (let i = currentYear; i >= currentYear - 100; i--) {
      years.push(i)
    }
    return years
  }, [currentDate])

  const monthOptions = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const getCalendarDays = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1)
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1

    const days: (number | null)[] = []

    for (let i = 0; i < adjustedStartingDay; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    const remainder = days.length % 7
    if (remainder !== 0) {
      const pad = 7 - remainder
      for (let i = 0; i < pad; i++) {
        days.push(null)
      }
    }

    return days
  }

  const calendarDays = getCalendarDays()

  const isCurrentMonthDay = (day: number | null) => day !== null

  const getDateFromCell = (day: number | null, isCurrentMonth: boolean): Date | null => {
    if (!day || !isCurrentMonth) return null
    return startOfDay(new Date(selectedYear, selectedMonth, day))
  }

  const isDateSelectable = (date: Date): boolean => {
    if (minDate && date < startOfDay(minDate)) return false
    if (maxDate && date > startOfDay(maxDate)) return false
    return true
  }

  const handleDateClick = (day: number | null, isCurrentMonth: boolean) => {
    const date = getDateFromCell(day, isCurrentMonth)
    if (!date || !isDateSelectable(date)) return

    if (mode === 'single') {
      setSelectedDate(date)
      onDateSelect?.(date)
      return
    }

    if (!rangeStart || rangeEnd) {
      setRangeStart(date)
      setRangeEnd(null)
      return
    }

    let start = rangeStart
    let end = date
    if (end < start) {
      const tmp = start
      start = end
      end = tmp
    }
    setRangeStart(start)
    setRangeEnd(end)
    onDateRangeSelect?.(start, end)
  }

  const isDateSelected = (day: number | null, isCurrentMonth: boolean) => {
    const date = getDateFromCell(day, isCurrentMonth)
    if (!date) return false

    if (mode === 'single') {
      if (!selectedDate) return false
      return isSameDay(selectedDate, date)
    }

    if (rangeStart && isSameDay(rangeStart, date)) return true
    if (rangeEnd && isSameDay(rangeEnd, date)) return true
    return false
  }

  const isDateInRangeSpan = (day: number | null, isCurrentMonth: boolean) => {
    if (mode !== 'range' || !rangeStart || !rangeEnd) return false
    const date = getDateFromCell(day, isCurrentMonth)
    if (!date) return false
    return date > rangeStart && date < rangeEnd
  }

  const isDateEnabled = (day: number | null, isCurrentMonth: boolean) => {
    const date = getDateFromCell(day, isCurrentMonth)
    if (!date) return false
    return isDateSelectable(date)
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value, 10))
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(parseInt(e.target.value, 10))
  }

  const isCompact = size === 'compact'
  const ui = getSizeStyles(isCompact)
  const weekDays = ui.weekDays

  const defaultRangeHint = !rangeStart
    ? intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_HINT_START'})
    : !rangeEnd
      ? intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_HINT_END'})
      : intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_HINT_DONE'})

  const selectBaseStyle: React.CSSProperties = {
    borderRadius: '4px',
    border: isDark ? '1px solid #4b5563' : '1px solid #ddd',
    cursor: 'pointer',
    backgroundColor: isDark ? '#374151' : '#fff',
    color: isDark ? '#ffffff' : '#333333',
    ...ui.select,
  }

  const {boxShadow: presetShadow, ...containerLayout} = ui.container
  const containerShadow =
    presetShadow ?? (isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)')

  return (
    <div
      style={{
        backgroundColor: isDark ? '#1f2937' : '#fff',
        border: isCompact ? (isDark ? '1px solid #4b5563' : '1px solid #e5e7eb') : undefined,
        ...containerLayout,
        boxShadow: containerShadow,
      }}
    >
      {mode === 'range' ? (
        <div
          style={{
            color: isDark ? '#9ca3af' : '#666',
            textAlign: isCompact ? 'center' : undefined,
            ...ui.hint,
          }}
        >
          {rangeHint || defaultRangeHint}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          ...ui.header,
        }}
      >
        <select value={selectedMonth} onChange={handleMonthChange} style={selectBaseStyle}>
          {monthOptions.map((month, index) => (
            <option
              key={index}
              value={index}
              style={{
                backgroundColor: isDark ? '#374151' : '#fff',
                color: isDark ? '#ffffff' : '#333333',
              }}
            >
              {month}
            </option>
          ))}
        </select>

        <select value={selectedYear} onChange={handleYearChange} style={selectBaseStyle}>
          {yearOptions.map((year) => (
            <option
              key={year}
              value={year}
              style={{
                backgroundColor: isDark ? '#374151' : '#fff',
                color: isDark ? '#ffffff' : '#333333',
              }}
            >
              {year}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: ui.gridGap,
          marginBottom: isCompact ? '4px' : '8px',
        }}
      >
        {weekDays.map((day) => (
          <div
            key={day}
            style={{
              textAlign: 'center',
              fontWeight: '500',
              color: isDark ? '#9ca3af' : '#666',
              ...ui.weekday,
            }}
          >
            {day}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: ui.gridGap,
        }}
      >
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={index} style={{height: ui.emptyCellHeight}} aria-hidden />
          }
          const isCurrentMonth = isCurrentMonthDay(day)
          const isSelected = isDateSelected(day, isCurrentMonth)
          const inSpan = isDateInRangeSpan(day, isCurrentMonth)
          const isEnabled = isDateEnabled(day, isCurrentMonth)

          let backgroundColor = isDark ? '#1f2937' : '#f5f5f5'
          if (isEnabled && isCurrentMonth) {
            if (isSelected) {
              backgroundColor = '#007bff'
            } else if (inSpan) {
              backgroundColor = isDark ? '#1e3a5f' : '#cfe2ff'
            } else {
              backgroundColor = isDark ? '#374151' : '#fff'
            }
          }

          return (
            <button
              key={index}
              type='button'
              onClick={() => handleDateClick(day, isCurrentMonth)}
              disabled={!isEnabled}
              style={{
                border: isSelected
                  ? '2px solid #007bff'
                  : day && isCurrentMonth
                    ? isDark
                      ? '1px solid #4b5563'
                      : '1px solid #e0e0e0'
                    : '1px solid transparent',
                borderRadius: isCompact ? '3px' : '4px',
                backgroundColor,
                color: isSelected
                  ? '#fff'
                  : day && isCurrentMonth && isEnabled
                    ? isDark
                      ? '#e5e7eb'
                      : '#000'
                    : '#ccc',
                cursor: isEnabled ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: day && isCurrentMonth && isEnabled ? 1 : 0.5,
                ...ui.day,
              }}
              onMouseEnter={(e) => {
                if (isEnabled && !isSelected && !inSpan) {
                  e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : '#e7f3ff'
                  e.currentTarget.style.borderColor = '#007bff'
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = backgroundColor
                  e.currentTarget.style.borderColor = isDark ? '#4b5563' : '#e0e0e0'
                }
              }}
            >
              {day}
            </button>
          )
        })}
      </div>

      {showSkip && onSkip ? (
        <div style={{marginTop: ui.skipMarginTop, textAlign: 'right'}}>
          <button
            type='button'
            onClick={onSkip}
            style={{
              background: 'none',
              border: 'none',
              color: isDark ? '#60a5fa' : '#007bff',
              cursor: 'pointer',
              textDecoration: 'underline',
              ...ui.skipButton,
            }}
          >
            Skip option
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default ChatbotDatePicker
